/**
 * 长按手势的 DOM 接线（任务 11b）：Pointer Events → longPress 状态机（11a）。
 * 照 useSwipeBack 的分层先例：状态机纯逻辑已单测，本 hook 只做事件挂载。
 *
 * 委托式：挂在列表容器上，onTrigger 带按下时的 target，由调用方自己
 * closest 到目标行——虚拟列表行频繁挂卸，不往每行上挂监听。
 *
 * 返回 callback ref（不是 RefObject）：宿主元素可能晚于首渲染才出现
 * （MessageList 加载态先渲染另一分支），对象 ref 的赋值不触发 effect 重跑，
 * 监听器会永远挂不上——元素进 state 作 effect 依赖才跟得上。
 *
 * enabled 时同时抑制容器内的 contextmenu：安卓长按会在 ~500ms 时派发
 * contextmenu（与我们的计时器几乎同刻），不拦会与自绘菜单双开；桌面布局
 * enabled=false，右键路径由调用方的 onContextMenu 自行处理。
 * 触发后的 pointerup 会紧跟一个 click（真机），capture 阶段吞掉，
 * 避免误触按下位置下方的按钮。
 */
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { createLongPressGesture } from './longPress';

export function useLongPress(
  enabled: boolean,
  onTrigger: (x: number, y: number, target: Element) => void,
): (el: HTMLDivElement | null) => void {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const onTriggerRef = useRef(onTrigger);
  useLayoutEffect(() => {
    onTriggerRef.current = onTrigger;
  });

  // useLayoutEffect 同步挂监听：useEffect 延迟到 rAF 后，挂上前的按下会漏
  useLayoutEffect(() => {
    if (!enabled || el === null) return;

    let downTarget: Element | null = null;
    let suppressClick = false;
    let suppressTimer: ReturnType<typeof setTimeout> | undefined;

    const gesture = createLongPressGesture({
      onTrigger(x, y) {
        if (downTarget !== null) onTriggerRef.current(x, y, downTarget);
      },
    });

    const onDown = (e: PointerEvent): void => {
      downTarget = e.target instanceof Element ? e.target : null;
      gesture.down(e.pointerId, e.clientX, e.clientY);
    };
    const onMove = (e: PointerEvent): void => gesture.move(e.pointerId, e.clientX, e.clientY);
    const onUp = (e: PointerEvent): void => {
      if (gesture.up(e.pointerId)) {
        suppressClick = true;
        clearTimeout(suppressTimer);
        // click 在 pointerup 同一任务内派发；有些情况不派发，宏任务兜底复位
        suppressTimer = setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
    };
    const onCancel = (): void => gesture.cancel();
    const onClickCapture = (e: MouseEvent): void => {
      if (suppressClick) {
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onContextMenu = (e: MouseEvent): void => e.preventDefault();

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
    el.addEventListener('click', onClickCapture, { capture: true });
    el.addEventListener('contextmenu', onContextMenu);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('click', onClickCapture, { capture: true });
      el.removeEventListener('contextmenu', onContextMenu);
      gesture.cancel();
      clearTimeout(suppressTimer);
    };
  }, [enabled, el]);

  return setEl;
}
